import React, { useState, Component, ErrorInfo, ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Terminal,
  ExternalLink,
  Info,
  Loader2,
  Play,
  PlusCircle,
  RefreshCw,
} from "lucide-react";
import {
  useCodespaceStatusQuery,
  CodespaceDetails,
} from "../../../hooks/queries/useAssignmentQueries";
import { resolveCodespaceLaunchUrl } from "./LaunchCodespaceButton";

export interface CodespaceActionButtonProps {
  /**
   * Submission ID used to query `GET /courses/assignments/<submission_id>/codespace-status/`.
   */
  submissionId?: number | string | null;
  /**
   * Optional submission object (can supply id, repository_name, repository_url, codespace_url,
   * or pre-populated codespace status).
   */
  submission?: {
    id?: number | string;
    repository_name?: string;
    repository_url?: string;
    codespace_url?: string | null;
    has_existing_codespace?: boolean;
    codespace?: CodespaceDetails | null;
    create_new_url?: string | null;
  } | null;
  /**
   * Full or short repository name (e.g. "Codehub-Technologies/python-codehub-assignments-bundle-username").
   */
  repoFullName?: string | null;
  /**
   * Direct fallback `codespace_url` for creating a new Codespace.
   */
  codespaceUrl?: string | null;
  /**
   * Repository HTML URL fallback (e.g. "https://github.com/Org/repo").
   */
  repoUrl?: string | null;
  /**
   * Default GitHub organization name.
   */
  defaultOrg?: string;
  /**
   * Target git branch (defaults to "main").
   */
  refBranch?: string;
  /**
   * Explicitly disable the button (e.g. before the repository is provisioned).
   */
  disabled?: boolean;
  /**
   * Show loading indicator while repository provisioning is running.
   */
  isLoading?: boolean;
  /**
   * Render helper note below the control.
   */
  showHelperNote?: boolean;
  /**
   * Button size preset.
   */
  size?: "sm" | "md";
  /**
   * Optional wrapper className.
   */
  className?: string;
}

const HELPER_TEXT =
  "Launches a pre-configured VS Code environment in your browser. No local installation required.";

/**
 * Maps a GitHub Codespace `state` string to a human-readable badge color & dot style.
 */
function getCodespaceStateBadge(state?: string | null): {
  label: string;
  pillClass: string;
  dotClass: string;
} {
  const normalized = String(state || "Available").trim();
  const lower = normalized.toLowerCase();

  if (lower === "available") {
    return {
      label: "Available",
      pillClass:
        "cht-bg-emerald-950/80 cht-text-emerald-300 cht-border cht-border-emerald-400/40",
      dotClass: "cht-bg-emerald-400",
    };
  }
  if (lower === "stopped" || lower === "shutdown") {
    return {
      label: normalized,
      pillClass:
        "cht-bg-amber-950/80 cht-text-amber-200 cht-border cht-border-amber-400/40",
      dotClass: "cht-bg-amber-400",
    };
  }
  return {
    label: normalized,
    pillClass:
      "cht-bg-sky-950/80 cht-text-sky-200 cht-border cht-border-sky-400/40",
    dotClass: "cht-bg-sky-400 cht-animate-pulse",
  };
}

/* ============================================================================
 * Error Boundary for Codespace Status / Action Rendering
 * ============================================================================ */
interface ErrorBoundaryProps {
  fallbackUrl: string | null;
  size: "sm" | "md";
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class CodespaceErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn("CodespaceActionButton error boundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const { fallbackUrl, size } = this.props;
      const sizeClasses =
        size === "sm"
          ? "cht-px-3.5 cht-py-2 cht-text-xs cht-rounded-xl cht-gap-1.5"
          : "cht-px-4 cht-py-2.5 cht-text-xs sm:cht-text-sm cht-rounded-[12px] cht-gap-2";

      if (!fallbackUrl) {
        return (
          <button
            type="button"
            disabled
            className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#21262d]/60 cht-text-slate-400 cht-border cht-border-slate-700/60 cht-cursor-not-allowed cht-opacity-65`}
          >
            <Terminal size={14} />
            <span className="cht-font-bold">Codespace Unavailable</span>
          </button>
        );
      }

      return (
        <a
          href={fallbackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#238636] hover:cht-bg-[#2ea043] cht-text-white cht-border cht-border-[#2ea043]/60 cht-font-bold cht-transition-colors cht-no-underline`}
        >
          <Terminal size={14} />
          <span>Launch in Codespaces</span>
          <ExternalLink size={12} />
        </a>
      );
    }

    return this.props.children;
  }
}

/* ============================================================================
 * Inner Interactive Component
 * ============================================================================ */
const CodespaceActionButtonInner: React.FC<CodespaceActionButtonProps> = ({
  submissionId,
  submission,
  repoFullName,
  codespaceUrl,
  repoUrl,
  defaultOrg = "Codehub-Technologies",
  refBranch = "main",
  disabled = false,
  isLoading = false,
  showHelperNote = true,
  size = "md",
  className = "",
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const resolvedSubmissionId = submissionId ?? submission?.id ?? null;
  const resolvedRepoName = repoFullName ?? submission?.repository_name ?? null;
  const resolvedRepoUrl = repoUrl ?? submission?.repository_url ?? null;
  const resolvedDirectCodespaceUrl =
    codespaceUrl ?? submission?.create_new_url ?? submission?.codespace_url ?? null;

  const {
    data: liveStatus,
    isLoading: isCheckingStatus,
    isFetching: isRefetchingStatus,
    refetch: refetchStatus,
  } = useCodespaceStatusQuery(resolvedSubmissionId, {
    enabled: Boolean(resolvedSubmissionId) && !disabled && !isLoading,
  });

  // Fallback URL to create a new Codespace
  const createNewUrl = resolveCodespaceLaunchUrl({
    codespaceUrl: liveStatus?.create_new_url || resolvedDirectCodespaceUrl,
    repoFullName: liveStatus?.repository_name || resolvedRepoName,
    repoUrl: resolvedRepoUrl,
    defaultOrg,
    refBranch,
  });

  const existingCodespace: CodespaceDetails | null =
    liveStatus?.codespace ?? submission?.codespace ?? null;

  const hasExistingCodespace = Boolean(
    (liveStatus?.has_existing_codespace ?? submission?.has_existing_codespace) &&
      existingCodespace?.web_url
  );

  const sizeClasses =
    size === "sm"
      ? "cht-px-3.5 cht-py-2 cht-text-xs cht-rounded-xl cht-gap-1.5"
      : "cht-px-4 cht-py-2.5 cht-text-xs sm:cht-text-sm cht-rounded-[12px] cht-gap-2";

  // 1. Neutral pulse placeholder while checking existing Codespaces status on initial load
  if (isCheckingStatus && !liveStatus && !submission?.codespace) {
    return (
      <div className={`cht-inline-flex cht-flex-col ${className}`}>
        <div
          aria-busy="true"
          aria-live="polite"
          className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#21262d] cht-text-slate-300 cht-border cht-border-[#30363d] cht-animate-pulse cht-select-none`}
        >
          <Loader2
            size={size === "sm" ? 13 : 15}
            className="cht-animate-spin cht-text-sky-400 cht-shrink-0"
          />
          <span className="cht-font-semibold cht-tracking-tight cht-whitespace-nowrap">
            Checking Workspace...
          </span>
        </div>
      </div>
    );
  }

  const isUnavailable =
    disabled || isLoading || (!hasExistingCodespace && !createNewUrl);

  const stateBadge = existingCodespace
    ? getCodespaceStateBadge(existingCodespace.state)
    : null;

  const primaryHref = hasExistingCodespace
    ? existingCodespace!.web_url
    : createNewUrl;

  return (
    <div className={`cht-inline-flex cht-flex-col ${className}`}>
      <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
        <div
          className="cht-relative cht-inline-flex cht-items-center"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
        >
          {isUnavailable || !primaryHref ? (
            <button
              type="button"
              disabled
              aria-disabled="true"
              title={HELPER_TEXT}
              className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#21262d]/60 cht-text-slate-400 cht-border cht-border-slate-700/60 cht-cursor-not-allowed cht-opacity-65 cht-select-none cht-transition-all`}
            >
              {isLoading ? (
                <Loader2
                  size={size === "sm" ? 13 : 15}
                  className="cht-animate-spin cht-shrink-0"
                />
              ) : (
                <Terminal size={size === "sm" ? 13 : 15} className="cht-shrink-0" />
              )}
              <span className="cht-font-bold cht-tracking-tight cht-whitespace-nowrap">
                {isLoading ? "Preparing Codespace..." : "Launch in Codespaces"}
              </span>
            </button>
          ) : hasExistingCodespace ? (
            /* State A: Existing Codespace Found -> "Resume Workspace" (#1f6feb) + Status Pill */
            <motion.a
              href={primaryHref}
              target="_blank"
              rel="noopener noreferrer"
              title={`Resume existing Codespace (${existingCodespace?.name || "workspace"}) — ${HELPER_TEXT}`}
              whileHover={{ scale: 1.015 }}
              whileTap={{ scale: 0.985 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#1f6feb] hover:cht-bg-[#388bfd] cht-text-white cht-border cht-border-[#388bfd]/60 cht-shadow-[0_2px_12px_rgba(31,111,235,0.28)] cht-transition-colors cht-no-underline focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#388bfd]/60`}
            >
              <Play
                size={size === "sm" ? 12 : 14}
                className="cht-fill-current cht-shrink-0"
              />
              <span className="cht-font-bold cht-tracking-tight cht-whitespace-nowrap">
                Resume Workspace
              </span>
              {stateBadge && (
                <span
                  className={`cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold ${stateBadge.pillClass}`}
                >
                  <span
                    className={`cht-w-1.5 cht-h-1.5 cht-rounded-full ${stateBadge.dotClass}`}
                  />
                  {stateBadge.label}
                </span>
              )}
              <ExternalLink
                size={size === "sm" ? 12 : 13}
                className="cht-opacity-85 cht-shrink-0"
              />
            </motion.a>
          ) : (
            /* State B: No Existing Codespace -> "Launch in Codespaces" (#238636) */
            <motion.a
              href={primaryHref}
              target="_blank"
              rel="noopener noreferrer"
              title={HELPER_TEXT}
              whileHover={{ scale: 1.015 }}
              whileTap={{ scale: 0.985 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`cht-inline-flex cht-items-center cht-justify-center ${sizeClasses} cht-bg-[#238636] hover:cht-bg-[#2ea043] cht-text-white cht-border cht-border-[#2ea043]/60 cht-shadow-[0_2px_10px_rgba(35,134,54,0.22)] cht-transition-colors cht-no-underline focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#2ea043]/60`}
            >
              <Terminal size={size === "sm" ? 13 : 15} className="cht-shrink-0" />
              <span className="cht-font-bold cht-tracking-tight cht-whitespace-nowrap">
                Launch in Codespaces
              </span>
              <ExternalLink
                size={size === "sm" ? 12 : 13}
                className="cht-opacity-85 cht-shrink-0"
              />
            </motion.a>
          )}

          {/* Hover / Focus Tooltip */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                role="tooltip"
                initial={{ opacity: 0, y: 6, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="cht-absolute cht-bottom-full cht-left-1/2 -cht-translate-x-1/2 cht-mb-2 cht-w-64 cht-p-2.5 cht-rounded-xl cht-bg-[#161b22] cht-text-slate-100 cht-border cht-border-[#30363d] cht-shadow-xl cht-z-30 cht-pointer-events-none"
              >
                <div className="cht-flex cht-items-start cht-gap-2">
                  <Info
                    size={13}
                    className="cht-text-emerald-400 cht-shrink-0 cht-mt-0.5"
                  />
                  <div className="cht-space-y-1">
                    {hasExistingCodespace && existingCodespace?.name && (
                      <p className="cht-text-[10px] cht-font-mono cht-text-sky-300 cht-truncate">
                        Instance: {existingCodespace.name}
                      </p>
                    )}
                    <p className="cht-text-[11px] cht-leading-snug cht-font-medium cht-text-slate-200">
                      {HELPER_TEXT}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Secondary Action when an existing Codespace exists: "Or create new instance" + Refresh */}
        {hasExistingCodespace && createNewUrl && (
          <div className="cht-inline-flex cht-items-center cht-gap-2">
            <a
              href={createNewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1.5 cht-rounded-lg cht-bg-[#21262d] hover:cht-bg-[#30363d] cht-border cht-border-[#30363d] cht-text-[11px] cht-font-semibold cht-text-slate-300 hover:cht-text-white cht-transition-colors cht-no-underline"
              title="Create a fresh Codespace instance"
            >
              <PlusCircle size={12} className="cht-text-slate-400" />
              <span>Or create new instance</span>
              <ExternalLink size={10} className="cht-opacity-75" />
            </a>

            {resolvedSubmissionId && (
              <button
                type="button"
                onClick={() => refetchStatus()}
                disabled={isRefetchingStatus}
                title="Refresh Codespace status"
                className="cht-p-1.5 cht-rounded-lg cht-bg-[#21262d] hover:cht-bg-[#30363d] cht-border cht-border-[#30363d] cht-text-slate-400 hover:cht-text-slate-200 cht-transition-colors"
              >
                <RefreshCw
                  size={12}
                  className={isRefetchingStatus ? "cht-animate-spin cht-text-sky-400" : ""}
                />
              </button>
            )}
          </div>
        )}
      </div>

      {showHelperNote && (
        <p className="cht-mt-1.5 cht-text-[11px] cht-text-slate-400 cht-flex cht-items-center cht-gap-1.5 cht-leading-snug">
          <Info size={11} className="cht-text-emerald-400 cht-shrink-0" />
          <span>
            {hasExistingCodespace
              ? `Existing Codespace detected (${existingCodespace?.state || "Ready"}). Resume directly to avoid duplicate containers.`
              : HELPER_TEXT}
          </span>
        </p>
      )}
    </div>
  );
};

export const CodespaceActionButton: React.FC<CodespaceActionButtonProps> = (
  props
) => {
  const fallbackUrl = resolveCodespaceLaunchUrl({
    codespaceUrl:
      props.codespaceUrl ??
      props.submission?.create_new_url ??
      props.submission?.codespace_url,
    repoFullName: props.repoFullName ?? props.submission?.repository_name,
    repoUrl: props.repoUrl ?? props.submission?.repository_url,
    defaultOrg: props.defaultOrg,
    refBranch: props.refBranch,
  });

  return (
    <CodespaceErrorBoundary fallbackUrl={fallbackUrl} size={props.size || "md"}>
      <CodespaceActionButtonInner {...props} />
    </CodespaceErrorBoundary>
  );
};

export default CodespaceActionButton;
